import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { api, resolveImage, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import useSeo from "@/hooks/useSeo";
import { Upload, X, Loader2, ArrowRight, ClipboardList, ImagePlus, BadgeEuro } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1494905998402-395d579af36f?q=80&w=2000";
const STEP_ICONS = [ClipboardList, ImagePlus, BadgeEuro];

const EMPTY = {
  name: "", email: "", phone: "", brand: "", model: "", year: "",
  mileage: "", fuel: "", transmission: "", condition: "", price_expectation: "",
  message: "", images: [], type: "ankauf", desired_vehicle: "",
};

export default function Ankauf({ mode = "ankauf" }) {
  const { t } = useLang();
  const isInz = mode === "inzahlung";
  useSeo({
    title: isInz
      ? "Inzahlungnahme Köln · Faires Angebot in 24 Stunden | DNA Automotive"
      : "Auto Ankauf Köln · Fahrzeug fair verkaufen | DNA Automotive",
    description: isInz
      ? "Fahrzeug in Zahlung geben und beim Neukauf anrechnen lassen – transparent, fair, unkompliziert. Kostenlose Bewertung durch Ihr Autohaus in der MotorWorld Köln."
      : "Fahrzeugankauf in Köln zu Bestpreisen: Sportwagen, Luxus, Premium-Gebrauchtwagen. Faires Angebot in 24 Stunden, sofortige Abholung deutschlandweit möglich.",
    path: isInz ? "/inzahlungnahme" : "/ankauf",
    breadcrumbs: [{ name: isInz ? "Inzahlungnahme" : "Ankauf", url: isInz ? "/inzahlungnahme" : "/ankauf" }],
  });
  const c = isInz ? t.inzahlung : t.ankauf;
  const f = t.ankauf.form;
  const [form, setForm] = useState({ ...EMPTY, type: mode });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => window.scrollTo(0, 0), []);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/ankauf/upload", fd);
        urls.push(data.url);
      }
      setForm((s) => ({ ...s, images: [...s.images, ...urls] }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/ankauf", form);
      toast.success(f.success);
      setForm({ ...EMPTY, type: mode });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || f.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-obsidian border border-white/15 rounded-none focus:border-brand-light focus:outline-none text-white placeholder-zinc-600 px-4 py-3 text-sm transition-colors";
  const labelCls = "text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block";

  return (
    <div className="bg-obsidian min-h-screen" data-testid={isInz ? "inzahlung-page" : "ankauf-page"}>
      <Header />

      {/* Hero */}
      <section className="relative pt-40 pb-20 px-6 md:px-10 overflow-hidden grain">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" style={{ objectPosition: "center 40%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/85 to-obsidian/60" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{c.overline}</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-white text-5xl md:text-7xl leading-[0.9] mb-6">{c.title}</h1>
          <p className="text-zinc-300 max-w-2xl text-lg">{c.subtitle}</p>
        </div>
      </section>

      {/* Steps */}
      <section className="px-6 md:px-10 -mt-4 relative z-10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {c.steps.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <div key={i} className="bg-obsidian p-7 flex items-start gap-4" data-testid={`ankauf-step-${i}`}>
                <span className="font-heading font-black text-brand-bright text-3xl">{i + 1}</span>
                <div>
                  <div className="flex items-center gap-2 text-white font-heading font-bold uppercase tracking-tight"><Icon className="w-4 h-4 text-brand-bright" />{s.t}</div>
                  <p className="text-zinc-400 text-sm mt-1">{s.d}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Form */}
      <section className="px-6 md:px-10 py-20">
        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-[1000px] mx-auto bg-obsidian-surface border border-white/10 p-8 md:p-12 space-y-10"
          data-testid="ankauf-form"
        >
          {/* Vehicle data */}
          <div>
            <h2 className="font-heading font-black uppercase tracking-tight text-brand-bright text-sm tracking-[0.25em] mb-6">{f.vehicleData}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><label className={labelCls}>{f.brand} *</label><input required value={form.brand} onChange={(e) => setField("brand", e.target.value)} className={inputCls} data-testid="ankauf-brand" /></div>
              <div><label className={labelCls}>{f.model} *</label><input required value={form.model} onChange={(e) => setField("model", e.target.value)} className={inputCls} data-testid="ankauf-model" /></div>
              <div><label className={labelCls}>{f.year}</label><input value={form.year} onChange={(e) => setField("year", e.target.value)} className={inputCls} data-testid="ankauf-year" /></div>
              <div><label className={labelCls}>{f.mileage}</label><input value={form.mileage} onChange={(e) => setField("mileage", e.target.value)} className={inputCls} data-testid="ankauf-mileage" /></div>
              <div><label className={labelCls}>{f.fuel}</label><input value={form.fuel} onChange={(e) => setField("fuel", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>{f.transmission}</label><input value={form.transmission} onChange={(e) => setField("transmission", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>{f.condition}</label><input value={form.condition} onChange={(e) => setField("condition", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>{f.price}</label><input value={form.price_expectation} onChange={(e) => setField("price_expectation", e.target.value)} className={inputCls} data-testid="ankauf-price" /></div>
            </div>
            {isInz && (
              <div className="mt-6"><label className={labelCls}>{c.desiredLabel}</label><input value={form.desired_vehicle} onChange={(e) => setField("desired_vehicle", e.target.value)} className={inputCls} placeholder={c.desiredPlaceholder} data-testid="inzahlung-desired" /></div>
            )}
          </div>

          {/* Images */}
          <div>
            <label className={labelCls}>{f.images}</label>
            <div className="flex flex-wrap gap-3 mb-2">
              {form.images.map((im, i) => (
                <div key={i} className="relative w-28 h-20 border border-white/10 overflow-hidden">
                  <img src={resolveImage(im)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-black/70 p-1 text-white hover:text-red-400" data-testid={`ankauf-remove-img-${i}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <label className="w-28 h-20 border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-brand-light transition-colors gap-1" data-testid="ankauf-upload">
                {uploading ? <Loader2 className="w-5 h-5 text-brand-bright animate-spin" /> : <Upload className="w-5 h-5 text-zinc-500" />}
                <span className="text-[10px] text-zinc-500 uppercase">{uploading ? f.uploading : "+"}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
              </label>
            </div>
            <p className="text-xs text-zinc-400">{f.uploadHint}</p>
          </div>

          {/* Contact data */}
          <div>
            <h2 className="font-heading font-black uppercase tracking-tight text-brand-bright text-sm tracking-[0.25em] mb-6">{f.yourData}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div><label className={labelCls}>{f.name} *</label><input required value={form.name} onChange={(e) => setField("name", e.target.value)} className={inputCls} data-testid="ankauf-name" /></div>
              <div><label className={labelCls}>{f.email} *</label><input required type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls} data-testid="ankauf-email" /></div>
              <div><label className={labelCls}>{f.phone}</label><input value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={inputCls} data-testid="ankauf-phone" /></div>
            </div>
            <div className="mt-6"><label className={labelCls}>{f.message}</label><textarea rows={3} value={form.message} onChange={(e) => setField("message", e.target.value)} className={inputCls + " resize-none"} data-testid="ankauf-message" /></div>
          </div>

          <button type="submit" disabled={sending || uploading} className="group inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-brand-hover transition-colors disabled:opacity-50" data-testid="ankauf-submit">
            {sending ? f.sending : f.submit}
            {!sending && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>
        </motion.form>
      </section>

      <Footer />
      <WhatsAppButton prefill={`Hallo DNA Automotive, ${isInz ? "ich möchte mein Fahrzeug in Zahlung geben (Inzahlungnahme)." : "ich möchte mein Fahrzeug verkaufen (Ankauf)."}`} />
    </div>
  );
}
